import { FAQ_TRANSLATIONS } from "./faq-translations/index.ts";

export interface FAQSection {
  category: string;
  items: { q: string; a: string }[];
}

export interface FAQPolicyValues {
  /** Localised carrier name for the most-shipped market (e.g. "PostNL"). */
  carrier: string;
  /** Free-form delivery window string (e.g. "2-5 werkdagen"). */
  deliveryWindow: string;
  /** Free-shipping threshold for the primary market. */
  primaryFreeAbove: number;
  /** ISO-2 of the primary market. */
  primaryCountry: string;
  /** Localised primary-country name. */
  primaryCountryName: string;
  /** Optional secondary country with its own free-shipping threshold
   *  (mentioned explicitly because customers ask about it most often). */
  secondaryFreeAbove: number | null;
  secondaryCountryName: string | null;
  /** Return window in days. */
  returnWindowDays: number;
  /** Return cost per package in EUR. */
  returnCost: number;
  /** Standard paid shipping rate (primary market, orders under the free
   *  threshold) in EUR. Feeds the Product JSON-LD offers.shippingRate. */
  costUnderThreshold: number;
}

/**
 * Sensible defaults so SSR renders something even before the backend
 * has answered. Matches what the storefront historically hardcoded.
 */
export const DEFAULT_FAQ_POLICY: FAQPolicyValues = {
  carrier: "PostNL",
  deliveryWindow: "2-5 werkdagen",
  primaryFreeAbove: 50,
  primaryCountry: "NL",
  primaryCountryName: "Nederland",
  secondaryFreeAbove: 95,
  secondaryCountryName: "België",
  returnWindowDays: 14,
  returnCost: 7.25,
  costUnderThreshold: 4.95,
};

const API_URL = process.env.NURANI_API_URL || "https://api.nurani.io";
const ORG_SLUG = process.env.NURANI_ORG_SLUG || "unity-cosmetics";

/**
 * Server-side fetch of the live shipping + returns policy. Single source
 * of truth so the FAQ page copy and the Product JSON-LD shippingDetails
 * / hasMerchantReturnPolicy (SEO audit 2026-05-17, MED-SEO-06) always
 * cite the exact same numbers the operator has configured. Falls back to
 * the platform defaults if any upstream request hiccups so neither the
 * FAQ page nor the structured data ever blanks.
 */
export async function loadFaqPolicy(): Promise<FAQPolicyValues> {
  const headers = { "x-organization-slug": ORG_SLUG };
  try {
    const [nlRes, beRes, returnsRes] = await Promise.all([
      fetch(`${API_URL}/api/public/shipping/quote?country=NL&weight=0&cartTotal=0`, {
        headers,
        next: { revalidate: 300, tags: ["shipping-quote-nl"] },
      }),
      fetch(`${API_URL}/api/public/shipping/quote?country=BE&weight=0&cartTotal=0`, {
        headers,
        next: { revalidate: 300, tags: ["shipping-quote-be"] },
      }),
      fetch(`${API_URL}/api/public/returns-policy`, {
        headers,
        next: { revalidate: 300, tags: ["returns-policy"] },
      }),
    ]);
    const nl = nlRes.ok ? await nlRes.json() : null;
    const be = beRes.ok ? await beRes.json() : null;
    const returns = returnsRes.ok ? await returnsRes.json() : null;
    return {
      carrier: nl?.cheapest?.carrier ?? DEFAULT_FAQ_POLICY.carrier,
      deliveryWindow: nl?.cheapest?.estimatedDays ?? DEFAULT_FAQ_POLICY.deliveryWindow,
      primaryFreeAbove:
        typeof nl?.cheapest?.freeAbove === "number"
          ? nl.cheapest.freeAbove
          : DEFAULT_FAQ_POLICY.primaryFreeAbove,
      primaryCountry: DEFAULT_FAQ_POLICY.primaryCountry,
      primaryCountryName: DEFAULT_FAQ_POLICY.primaryCountryName,
      secondaryFreeAbove:
        typeof be?.cheapest?.freeAbove === "number"
          ? be.cheapest.freeAbove
          : DEFAULT_FAQ_POLICY.secondaryFreeAbove,
      secondaryCountryName: be?.cheapest
        ? DEFAULT_FAQ_POLICY.secondaryCountryName
        : null,
      returnWindowDays:
        typeof returns?.windowDays === "number"
          ? returns.windowDays
          : DEFAULT_FAQ_POLICY.returnWindowDays,
      returnCost:
        typeof returns?.costPerPackage === "number"
          ? returns.costPerPackage
          : DEFAULT_FAQ_POLICY.returnCost,
      costUnderThreshold:
        typeof nl?.cheapest?.cost === "number" && nl.cheapest.cost > 0
          ? nl.cheapest.cost
          : DEFAULT_FAQ_POLICY.costUnderThreshold,
    };
  } catch {
    return DEFAULT_FAQ_POLICY;
  }
}

function eur(n: number): string {
  return n.toFixed(2).replace(/\.00$/, "").replace(".", ",");
}

/** Unit word for a delivery window, per locale. */
const WORKING_DAYS: Record<string, string> = {
  nl: "werkdagen",
  de: "Werktage",
  en: "working days",
  fr: "jours ouvrables",
  es: "días laborables",
  it: "giorni lavorativi",
};

/**
 * Re-renders a delivery window in the target language while keeping the
 * operator's numbers.
 *
 * deliveryWindow arrives from the NL shipping quote as free-form text
 * the operator typed ("2-5 werkdagen"). Interpolated raw into German
 * copy it produced "innerhalb von 2-5 werkdagen", the same leak the AGB
 * page carries. Only the unit word is swapped; the range itself is
 * backend truth and is never invented here. A window we cannot parse is
 * passed through untouched rather than guessed at.
 */
export function localizeDeliveryWindow(window: string, locale: string): string {
  // deliveryWindow comes from untyped upstream JSON, so the string type
  // is an assertion rather than a guarantee.
  if (typeof window !== "string") return "";
  const unit = WORKING_DAYS[locale];
  if (!unit || locale === "nl") return window;
  // Only the exact shape we recognise is rewritten: a count or range of
  // Dutch working days and nothing else. An earlier version matched any
  // digits anywhere and relabelled the unit, which turned "24 uur" into
  // "24 Werktage" and quietly dropped the upper bound of "3 tot 5
  // werkdagen". This is a delivery promise on a customer-facing page, so
  // an operator sentence we cannot parse with certainty is returned
  // untouched rather than reworded into a different promise.
  const parsed = window.trim().match(/^(\d+)\s*(?:-\s*(\d+))?\s*werkdag(?:en)?$/i);
  if (!parsed) return window;
  const range = parsed[2] ? `${parsed[1]}-${parsed[2]}` : parsed[1];
  return `${range} ${unit}`;
}

/**
 * Build the FAQ sections from live policy values so the answers don't
 * drift out of sync with shipping_methods / org.config.returns. The
 * function is pure so it can be called from a server component (SSR
 * for JSON-LD) and a client component without duplicate fetching.
 *
 * Dutch is the master copy; de/en/fr/es/it come from
 * ./faq-translations, which mirror this structure question for question
 * and interpolate the same policy values. An unknown locale falls back
 * to Dutch rather than blanking the page.
 *
 * A note for whoever edits the shipping and returns answers: every
 * number in them must keep coming from `p`. The threshold that shipped
 * as a literal in a translation string is what let German pages promise
 * free shipping at 50 while the customer was charged to 75
 * (scripts/test-shipping-copy-truth.mjs, rule 1). The policy itself is
 * still resolved for NL and BE only, so each locale's copy names the
 * country its terms apply to instead of implying they are the reader's.
 */
export function buildFaqs(
  p: FAQPolicyValues = DEFAULT_FAQ_POLICY,
  locale: string = "nl",
): FAQSection[] {
  const translated = FAQ_TRANSLATIONS[locale];
  if (translated) {
    return translated(p, {
      eur,
      deliveryWindow: localizeDeliveryWindow(p.deliveryWindow, locale),
    });
  }
  return buildDutchFaqs(p);
}

function buildDutchFaqs(p: FAQPolicyValues): FAQSection[] {
  const secondaryLine =
    p.secondaryCountryName && p.secondaryFreeAbove
      ? ` Voor ${p.secondaryCountryName} geldt gratis verzending vanaf €${eur(p.secondaryFreeAbove)}.`
      : "";
  return [
    {
      category: "Producten & allergien",
      items: [
        {
          q: "Ik heb een allergie, zijn jullie producten geschikt?",
          a: "In bijna alle gevallen zijn onze producten geschikt. Stuur ons een e-mail met je specifieke allergie en we adviseren je persoonlijk welke producten veilig zijn voor jou. Je kunt ook eerst een sample bestellen om het product op een klein plekje huid te testen.",
        },
        {
          q: "Wat is de betekenis van hypoallergeen?",
          a: "Hypoallergeen betekent dat de kans op een allergische reactie uitermate klein is. Alle producten van Unity Cosmetics zijn hypoallergeen. We kiezen bewust voor ingredienten die zo min mogelijk risico op irritatie met zich meebrengen.",
        },
        {
          q: "Hoe kan ik testen of een product geschikt is voor mij?",
          a: "We raden aan om het product eerst uit te proberen op een plekje aan de binnenkant van je elleboog. Als je een overgevoeligheid of allergische reactie krijgt, uit zich dat over het algemeen binnen 24 uur in de vorm van jeuk, lichte roodheid en soms kleine bultjes. Van de meeste producten zijn samples verkrijgbaar.",
        },
        {
          q: "Zijn jullie producten vegan?",
          a: "Een groot deel van ons assortiment is veganistisch. Op de productpagina staat bij de classificaties vermeld of het product veganistisch is. Alle producten zijn dierproefvrij.",
        },
        {
          q: "Bevatten jullie producten gluten?",
          a: "Al onze producten zijn glutenvrij. Dit staat ook vermeld bij de classificaties op de productpagina.",
        },
      ],
    },
    {
      category: "Bestellen & betalen",
      items: [
        {
          q: "Welke betaalmethoden accepteren jullie?",
          a: "We accepteren iDEAL, Visa, Mastercard, PayPal, Klarna (achteraf betalen), Bancontact, en SEPA-overboeking.",
        },
        {
          q: "Wanneer ontvang ik mijn bestelling?",
          a: `Bestellingen worden binnen ${p.deliveryWindow} bezorgd via ${p.carrier} met Track & Trace. Je ontvangt een bevestigingsmail met trackingcode zodra je pakket is verzonden.`,
        },
        {
          q: "Is er gratis verzending?",
          a: `Ja, bij bestellingen vanaf €${eur(p.primaryFreeAbove)} is verzending binnen ${p.primaryCountryName} gratis.${secondaryLine}`,
        },
        {
          q: "Kan ik ook samples bestellen?",
          a: "Ja, van de meeste producten zijn samples verkrijgbaar. Zo kun je het product eerst op je huid testen voordat je de volledige maat koopt.",
        },
      ],
    },
    {
      category: "Retourneren",
      items: [
        {
          q: "Kan ik een product retourneren?",
          a: `Je hebt ${p.returnWindowDays} dagen bedenktijd na ontvangst van je bestelling. Producten dienen ongeopend en in de originele verpakking te worden geretourneerd. Retourkosten zijn voor eigen rekening (ca. €${eur(p.returnCost)}).`,
        },
        {
          q: "Wat als ik een allergische reactie krijg?",
          a: "Neem direct contact met ons op. Hoewel de kans bij onze producten uitermate klein is, nemen we elke reactie serieus. We helpen je met een passende oplossing.",
        },
      ],
    },
  ];
}

/**
 * Default static FAQs for back-compat — exported so callers that
 * haven't migrated to the policy-aware build function yet keep
 * working with the platform defaults.
 *
 * @deprecated Prefer `buildFaqs(policy)` so the answers stay in sync
 * with shipping_methods / org.config.returns.
 */
export const faqs: FAQSection[] = buildFaqs(DEFAULT_FAQ_POLICY);
